import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, Plus } from "lucide-react";
import "./scheduling.css";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import BookingModal from "../../components/scheduling/BookingModal";
import Toast from "../../components/scheduling/Toast";

function ymd(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

export default function SpaceTimeline() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { companyId } = useCompany();

  const [space, setSpace] = useState(null);
  const [spaces, setSpaces] = useState([]); // for BookingModal dropdown
  const [bookings, setBookings] = useState([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");

  const [date, setDate] = useState(() => ymd(new Date()));

  const [toast, setToast] = useState({
    open: false,
    type: "info",
    message: "",
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [defaults, setDefaults] = useState({ start: null, end: null });

  const dayStart = useMemo(() => new Date(`${date}T00:00:00`), [date]);
  const dayEnd = useMemo(() => new Date(`${date}T23:59:59`), [date]);

  async function load() {
    if (!companyId || !id) return;
    setBusy(true);
    setErr("");
    try {
      // load spaces list (for modal)
      const { data: sData, error: sErr } = await supabase
        .from("spaces")
        .select("*")
        .eq("company_id", companyId)
        .order("name");
      if (sErr) throw sErr;
      setSpaces(sData || []);

      const found = (sData || []).find((s) => s.id === id) || null;
      setSpace(found);

      // load bookings for this space within day window
      const startIso = dayStart.toISOString();
      const endIso = dayEnd.toISOString();

      const { data: bData, error: bErr } = await supabase
        .from("space_bookings")
        .select("*")
        .eq("company_id", companyId)
        .eq("space_id", id)
        .eq("status", "confirmed")
        .lt("start_at", endIso)
        .gt("end_at", startIso)
        .order("start_at", { ascending: true });

      if (bErr) throw bErr;
      setBookings(bData || []);
    } catch (e) {
      setErr(e?.message || "Failed to load schedule");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, id, date]);

  useEffect(() => {
    if (!companyId || !id) return;
    const channel = supabase
      .channel("sched_space_timeline")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "space_bookings",
          filter: `company_id=eq.${companyId}`,
        },
        () => load()
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, id, date]);

  // Render on a 24h bar
  const totalMs = useMemo(
    () => dayEnd.getTime() - dayStart.getTime(),
    [dayStart, dayEnd]
  );

  const nowLine = useMemo(() => {
    const now = new Date();
    if (ymd(now) !== date) return null;
    const p = (now.getTime() - dayStart.getTime()) / totalMs;
    return clamp(p, 0, 1);
  }, [date, dayStart, totalMs]);

  const onClickBar = (e) => {
    if (!space || space.status !== "active") {
      setToast({
        open: true,
        type: "error",
        message: "This space is inactive and cannot be booked",
      });
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const p = clamp(x / rect.width, 0, 1);

    // snap to 30 mins
    const minutes = Math.round((p * 24 * 60) / 30) * 30;
    const start = new Date(dayStart);
    start.setMinutes(minutes);
    start.setSeconds(0, 0);

    const defDur = space.default_duration_min || 60;
    const end = new Date(start.getTime() + defDur * 60000);

    setDefaults({ start, end });
    setModalOpen(true);
  };

  return (
    <div className="sched-card">
      <div className="sched-card-head">
        <div>
          <button
            className="sched-btn ghost"
            onClick={() => navigate("/scheduling/spaces")}
          >
            <ChevronLeft size={16} /> Back
          </button>
          <h3 className="sched-h3" style={{ marginTop: 10 }}>
            {space ? space.name : "Space schedule"}
          </h3>
          <p className="sched-muted">
            Day timeline. Click the bar to start a booking draft.
          </p>
        </div>

        <div className="sched-row gap">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <button
            className="sched-btn primary"
            onClick={() => {
              if (!space || space.status !== "active") {
                setToast({
                  open: true,
                  type: "error",
                  message: "Space is inactive",
                });
                return;
              }
              const start = new Date(`${date}T09:00:00`);
              const end = new Date(
                start.getTime() + (space.default_duration_min || 60) * 60000
              );
              setDefaults({ start, end });
              setModalOpen(true);
            }}
          >
            <Plus size={16} /> New booking
          </button>
        </div>
      </div>

      {busy ? (
        <div className="sched-muted">Loading…</div>
      ) : err ? (
        <div className="sched-error">{err}</div>
      ) : !space ? (
        <div className="sched-empty">
          <div className="sched-empty-title">Space not found</div>
          <div className="sched-muted">
            It may have been deleted or you do not have access.
          </div>
        </div>
      ) : (
        <>
          {space.status !== "active" ? (
            <div className="sched-note">
              <span>
                <b>Inactive:</b> {space.inactive_reason || "—"}
              </span>
            </div>
          ) : null}

          <div className="sched-timeline">
            <div className="sched-timeline-scale">
              {Array.from({ length: 25 }).map((_, i) => (
                <span key={i}>
                  {i === 24 ? "24:00" : `${String(i).padStart(2, "0")}:00`}
                </span>
              ))}
            </div>

            <div
              className="sched-timeline-bar"
              onClick={onClickBar}
              role="button"
              tabIndex={0}
            >
              {nowLine !== null ? (
                <div
                  className="sched-now-line"
                  style={{ left: `${nowLine * 100}%` }}
                />
              ) : null}

              {bookings.map((b) => {
                const start = new Date(b.start_at).getTime();
                const end = new Date(b.end_at).getTime();

                const p1 = clamp((start - dayStart.getTime()) / totalMs, 0, 1);
                const p2 = clamp((end - dayStart.getTime()) / totalMs, 0, 1);

                const left = p1 * 100;
                const width = Math.max((p2 - p1) * 100, 0.5);

                const title = b.private_mode ? "Busy" : b.title || "—";

                return (
                  <div
                    key={b.id}
                    className="sched-timeline-block"
                    style={{ left: `${left}%`, width: `${width}%` }}
                    title={`${title}\n${new Date(b.start_at).toLocaleTimeString(
                      [],
                      { hour: "2-digit", minute: "2-digit" }
                    )} - ${new Date(b.end_at).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}`}
                  >
                    <div className="sched-timeline-title">{title}</div>
                    <div className="sched-timeline-time">
                      {new Date(b.start_at).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}{" "}
                      -{" "}
                      {new Date(b.end_at).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="sched-muted" style={{ marginTop: 10 }}>
              Note: buffer conflict is enforced at save-time by the database
              (effective window).
            </div>
          </div>
        </>
      )}

      <BookingModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        mode="create"
        spaces={spaces.filter((s) => s.status === "active")}
        defaultSpaceId={id}
        defaultStart={defaults.start}
        defaultEnd={defaults.end}
        onSaved={() => load()}
      />

      <Toast
        open={toast.open}
        type={toast.type}
        message={toast.message}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
      />
    </div>
  );
}
